import React, { useCallback, useState } from "react";
import { toast } from "sonner";
import { PartyPopper, Eye, Download, MessageCircle, RotateCw, Gift } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { certError, downloadCert, location } from "@/lib/certs";
import { CertViewDialog } from "@/components/certs/CertViewDialog";
import { CertShareDialog } from "@/components/certs/CertShareDialog";

// partner: { kind, name, code, city, state, certificate, certificate_error }
export const OnboardingSuccessDialog = ({ partner, onClose, onWelcomeKit }) => {
  const { can } = useApp();
  const [cert, setCert] = useState(null);
  const [err, setErr] = useState(null);
  const [view, setView] = useState(null);
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState(false);
  const closeView = useCallback(() => setView(null), []);
  const closeShare = useCallback(() => setShare(null), []);
  const c = cert || partner?.certificate;
  const error = err ?? (c?.pdf_status === "failed" ? c.pdf_error : partner?.certificate_error);

  const retry = async () => {
    setBusy(true);
    try {
      const { data } = c ? await api.post(`/certificates/${c.id}/regenerate-pdf`)
        : await api.post(`/certificates/partner/${partner.kind}/${partner.id}/issue`);
      setCert(data.certificate); setErr(data.error || null);
      if (!data.error) toast.success("Certificate ready");
    } catch (e) { setErr(await certError(e)); }
    setBusy(false);
  };
  const close = () => { setCert(null); setErr(null); onClose(); };
  const ready = c && c.pdf_status === "ready";
  const status = !c ? "Not generated" : ready ? `Issued · ${c.certificate_number}` : `Issued · ${c.certificate_number} (PDF pending)`;

  return (
    <Dialog open={!!partner} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md bg-card" data-testid="onboarding-success-dialog">
        <div className="flex flex-col items-center text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-500"><PartyPopper className="h-7 w-7" /></div>
          <DialogTitle className="mt-3 font-head text-lg font-bold" data-testid="onboarding-success-title">Congratulations! Onboarding Completed Successfully.</DialogTitle>
        </div>
        <div className="space-y-1.5 rounded-lg border border-border bg-background p-3 text-sm">
          <Row k="Partner Name" v={partner?.name} t="name" />
          <Row k={partner?.kind === "dp" ? "Connect Tym ID" : "Franchise ID"} v={partner?.code} t="code" />
          <Row k="Location" v={location(partner)} t="location" />
          <Row k="Certificate Status" v={status} t="cert-status" />
        </div>
        {error && <p className="rounded-lg bg-rose-500/10 p-2.5 text-xs text-rose-500" data-testid="onboarding-success-error">{error}</p>}
        <div className="flex flex-wrap justify-center gap-2">
          {ready && can("cert.download") && <Button variant="outline" onClick={() => setView(c)} data-testid="onboarding-view-cert"><Eye className="mr-1.5 h-4 w-4" /> View Certificate</Button>}
          {ready && can("cert.download") && <Button variant="outline" onClick={async () => { try { await downloadCert(c); } catch (e) { toast.error(await certError(e)); } }} data-testid="onboarding-download-cert"><Download className="mr-1.5 h-4 w-4" /> Download PDF</Button>}
          {ready && can("cert.share") && <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShare(c)} data-testid="onboarding-share-cert"><MessageCircle className="mr-1.5 h-4 w-4" /> Share on WhatsApp</Button>}
          {!ready && (c || partner?.certificate_error) && <Button onClick={retry} disabled={busy} data-testid="onboarding-retry-cert"><RotateCw className="mr-1.5 h-4 w-4" /> Retry</Button>}
          {onWelcomeKit && <Button variant="ghost" onClick={onWelcomeKit} data-testid="onboarding-welcome-kit"><Gift className="mr-1.5 h-4 w-4" /> Welcome Kit</Button>}
        </div>
        <CertViewDialog cert={view} onClose={closeView} />
        <CertShareDialog cert={share} onClose={closeShare} />
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ k, v, t }) => (
  <div className="flex justify-between gap-3"><span className="text-muted-foreground">{k}</span><span className="text-right font-semibold" data-testid={`onboarding-success-${t}`}>{v || "-"}</span></div>
);
